// src/portal-app/tabs/data/useOperationsData.js
//
// Data hook for the Operations tab landing (Task 4). Isolates every network
// call behind the shared { status, data, reload } envelope so the screen is
// pure presentation.
//
// Field provenance — deliberately split across primary/secondary:
//   - dashboardService.getStats() (PRIMARY) supplies revenueChangePct
//     (stats.kpis.revenue.change_pct — dashboard-stats' own field name,
//     NOT reportingService's `change_percent`), openTills (stats.sessions.
//     active, already date-scoped — no separate session call needed),
//     hasActiveSession and salesDate.
//   - transactionService.getTransactionsRange (SECONDARY) supplies
//     revenue/orders/cash/card from its range-wide `summary` — the exact
//     source the Transactions tab itself reads, so the two surfaces can
//     never disagree on "today's revenue".
//   - voidService.getVoids (SECONDARY) supplies voidsToday. Its `end_date`
//     MUST carry a ' 23:59:59' suffix: the endpoint filters on `deleted_at`,
//     a timestamp column, and a bare date silently drops every same-day
//     void logged after midnight.
//   - reservationService.list (SECONDARY, feature-gated) supplies
//     noShowsToday. When the 'reservations' feature is off, the call is
//     never fired and the field is `null` (absent), never `0` — the UI
//     must be able to omit the row rather than assert "0 no-shows".
//   - stats.revenue_trend (part of the PRIMARY dashboardService.getStats()
//     response — no extra request) supplies `trend`, a 7-entry
//     [{date, revenue}] series flattened to plain numbers. The .map is
//     required, not cosmetic: Sparkline.js calls Math.max(...points)
//     directly, so handing it the raw objects would produce NaN path
//     geometry — a broken chart, not no chart.
//
// Every call — primary and secondary alike — is fired in ONE
// Promise.allSettled batch. Only the primary's rejection sets
// status: 'error'; any secondary's rejection degrades just the field(s) it
// feeds (numbers → null, never a lying zero).
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    dashboardService,
    transactionService,
    voidService,
    reservationService,
} from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { toLocalISO } from '../../nav/dateRange';

// Stable fallback so a missing hasFeature never changes identity across
// renders (an inline `() => true` would recreate `load` every render and
// spin the effect below).
const FEATURE_DEFAULT_ON = () => true;

export function useOperationsData() {
    const auth = useAuth() || {};
    const hasFeature = typeof auth.hasFeature === 'function' ? auth.hasFeature : FEATURE_DEFAULT_ON;

    const [status, setStatus] = useState('loading');
    const [data, setData] = useState(null);
    const seqRef = useRef(0);

    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');

        const today = toLocalISO(new Date());
        const reservationsOn = hasFeature('reservations');

        const calls = [
            { key: 'stats', promise: dashboardService.getStats() },
            {
                key: 'tx',
                promise: transactionService.getTransactionsRange({
                    startDate: today,
                    endDate: today,
                    limit: 1,
                }),
            },
            {
                key: 'voids',
                promise: voidService.getVoids({
                    start_date: today,
                    end_date: `${today} 23:59:59`,
                    page_size: 1,
                }),
            },
        ];
        if (reservationsOn) {
            calls.push({
                key: 'noshows',
                promise: reservationService.list({ date: today, status: 'no_show' }),
            });
        }

        Promise.allSettled(calls.map((c) => c.promise)).then((results) => {
            if (seq !== seqRef.current) return; // stale — a faster reload already won

            const byKey = {};
            calls.forEach((c, i) => { byKey[c.key] = results[i]; });

            const statsResult = byKey.stats;
            if (!statsResult || statsResult.status !== 'fulfilled') {
                setData(null);
                setStatus('error');
                return;
            }
            const stats = statsResult.value || {};

            const txResult = byKey.tx;
            const summary = (txResult && txResult.status === 'fulfilled' && txResult.value)
                ? txResult.value.summary || null
                : null;

            const voidsResult = byKey.voids;
            const voids = (voidsResult && voidsResult.status === 'fulfilled')
                ? voidsResult.value
                : null;

            const noShowsResult = byKey.noshows;
            const noShows = (reservationsOn && noShowsResult && noShowsResult.status === 'fulfilled')
                ? noShowsResult.value
                : null;

            setData({
                revenue: summary ? Number(summary.revenue) || 0 : null,
                revenueChangePct: Number(stats?.kpis?.revenue?.change_pct) || 0,
                orders: summary ? Number(summary.count) || 0 : null,
                cash: summary ? Number(summary.cash) || 0 : null,
                card: summary ? Number(summary.card) || 0 : null,
                openTills: Number(stats?.sessions?.active) || 0,
                voidsToday: voids ? Number(voids.total) || 0 : null,
                noShowsToday: (reservationsOn && Array.isArray(noShows)) ? noShows.length : null,
                hasActiveSession: !!stats.has_active_session,
                salesDate: stats.sales_date || today,
                trend: (stats.revenue_trend || []).map((d) => Number(d.revenue) || 0),
            });
            setStatus('ready');
        });
    }, [hasFeature]);

    useEffect(() => { load(); }, [load]);

    return { status, data, reload: load };
}

export default useOperationsData;
